/* eslint-disable @typescript-eslint/no-explicit-any */
import { useId, useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { isFunction } from '../utils/is-type';

export type SetLocationState<State> = (
  value: State | ((prevState: State) => State),
  options?: { replace?: boolean; preventScrollReset?: boolean },
) => void;

/**
 * React Router의 location.state를 저장소로 활용하는 상태 관리 훅입니다.
 * 상태가 변경될 때마다 히스토리 스택을 쌓을 수 있다는 특징을 갖고 있습니다.
 * 팝업 등을 구현할 때 뒤로가기 동작을 지원하기 위해 주로 사용합니다.
 * 히스토리에 상태를 저장하는 LocationState의 특성상 동시성 문제가 발생할 수 있습니다.
 * 가급적 동시에 사용하는 일이 없도록 주의해주세요.
 */
export function useLocationState<State>(initialState?: State) {
  const id = useId();
  const location = useLocation();
  const navigate = useNavigate();

  const state: State = useMemo(() => {
    if (!location?.state?.[id]) {
      return initialState;
    }
    return location?.state?.[id];
  }, [id, location?.state, initialState]);

  const setState: SetLocationState<State> = (newValue, options) => {
    const value = isFunction(newValue) ? newValue(state) : newValue;
    navigate(
      { pathname: location.pathname, search: location.search },
      {
        state: { ...location?.state, [id]: value },
        replace: options?.replace,
        preventScrollReset: options?.preventScrollReset === false ? false : true,
      },
    );
  };

  return [state, setState] as const;
}
